import { ArrowDownRight, ArrowUpRight, DollarSign, Percent, ShoppingCart, TrendingUp } from 'lucide-react'
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { currentMonthForecast, num, optionalNum, type HomeSummary } from './model'

type Change = { text: string; up: boolean } | null

function percentChange(current: number, previous: number): Change {
  if (!previous) return null
  const change = ((current - previous) / Math.abs(previous)) * 100
  return { text: `${formatPercent(Math.abs(change))} vs prior 12 months`, up: change >= 0 }
}

function pointChange(current: number | null, previous: number | null): Change {
  if (current == null || previous == null) return null
  const change = current - previous
  return { text: `${Math.abs(change).toFixed(1)} pts vs prior 12 months`, up: change >= 0 }
}

export function KpiCards({ summary }: { summary: HomeSummary }) {
  const now = summary.financials_12m
  const before = summary.financials_prior_12m
  const months = summary.monthly ?? []
  const margin = optionalNum(now?.average_margin)
  const forecast = currentMonthForecast(months)
  const kpis = [
    {
      label: 'Revenue',
      value: formatMoney(now?.total_revenue),
      sub: `${formatMoney(now?.total_refunded)} refunded`,
      icon: DollarSign,
      change: percentChange(num(now?.total_revenue), num(before?.total_revenue)),
      points: months.map((month) => num(month.total_revenue)),
      forecast: forecast?.revenue,
    },
    {
      label: 'Profit',
      value: formatMoney(now?.total_profit),
      sub: `${formatMoney(now?.total_net)} Net`,
      icon: TrendingUp,
      change: percentChange(num(now?.total_profit), num(before?.total_profit)),
      points: months.map((month) => num(month.total_profit)),
      forecast: forecast?.profit,
    },
    {
      label: 'Margin',
      value: formatPercent(margin),
      sub: `${formatPercent(now?.booked_margin)} as booked`,
      icon: Percent,
      change: pointChange(margin, optionalNum(before?.average_margin)),
      points: months.map((month) => optionalNum(month.average_margin)),
    },
    {
      label: 'Orders',
      value: (now?.order_count ?? 0).toLocaleString('en-US'),
      sub: `${formatMoney(now?.average_order_value)} average order`,
      icon: ShoppingCart,
      change: percentChange(now?.order_count ?? 0, before?.order_count ?? 0),
      points: months.map((month) => month.order_count ?? 0),
      forecast: forecast?.orders,
    },
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {kpis.map((kpi) => (
        <Card key={kpi.label} className="gap-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-muted-foreground">{kpi.label}</CardTitle>
            <kpi.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{kpi.value}</div>
            <p className="text-xs text-muted-foreground">{kpi.sub}</p>
            {kpi.change ? (
              <p
                className={cn(
                  'mt-1 flex items-center gap-0.5 text-xs font-medium',
                  kpi.change.up ? 'text-emerald-700' : 'text-red-700',
                )}
              >
                {kpi.change.up ? (
                  <ArrowUpRight className="h-3.5 w-3.5" aria-label="Up" />
                ) : (
                  <ArrowDownRight className="h-3.5 w-3.5" aria-label="Down" />
                )}
                {kpi.change.text}
              </p>
            ) : null}
            <Sparkline points={kpi.points} forecast={kpi.forecast} label={`${kpi.label} by month, last 12 months`} />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// With a forecast, the current month is drawn as a dashed step from last month to its forecast.
function Sparkline({ points, forecast, label }: { points: (number | null)[]; forecast?: number; label: string }) {
  const last = points.length - 1
  const data = points.map((value, index) => ({
    index,
    value: forecast != null && index === last ? null : value,
    forecast: forecast == null ? null : index === last ? forecast : index === last - 1 ? value : null,
  }))
  return (
    <div className="mt-3 h-8 w-full" role="img" aria-label={label}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="oklch(0.556 0 0)"
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
          {forecast != null ? (
            <Line
              type="monotone"
              dataKey="forecast"
              stroke="oklch(0.556 0 0)"
              strokeWidth={1.5}
              strokeDasharray="3 2"
              dot={false}
              isAnimationActive={false}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
